import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';

import {
  PeriodBarRow,
  PeriodToggleGroup,
  PeriodToggleButton,
  PeriodCenter,
  PeriodBaseLabel,
  PeriodArrow,
  PeriodChipAnchor,
  PeriodChip,
  PeriodChipText,
  PeriodChipDays,
  PeriodChipCaret,
  PeriodPopover,
  PeriodPopHeader,
  PeriodPopMonth,
  PeriodPopNav,
  PeriodPopNavBtn,
  SegmentTrack,
  PeriodModeTab,
  PeriodCalWrap,
  PeriodCalRow,
  PeriodCalWeekCell,
  PeriodCalCell,
  PeriodDialWrap,
  PeriodDialHighlight,
  PeriodDialCols,
  PeriodDialCol,
  PeriodDialItem,
  PeriodManualBox,
  PeriodManualHead,
  PeriodManualLabel,
  PeriodManualHint,
  PeriodManualRow,
  PeriodInputBox,
  PeriodInput,
  PeriodAimBtn,
  PeriodManualTilde,
  PeriodConfirmBtn,
} from './SalesManagTab1Styled';

// 기간은 필터 패널에서 빼내 화면 맨 위 한 곳에서만 관리한다.
// 일일/주간/월간 토글 · 화살표 · 팝오버(달력/다이얼/직접 입력)가 모두 같은 범위를 갱신한다.
const TABS = [
  { key: 'day', label: '일일', position: 'left' },
  { key: 'week', label: '주간', position: 'mid' },
  { key: 'month', label: '월간', position: 'right' },
];

const MODES = [
  { key: 'cal', label: '달력' },
  { key: 'dial', label: '다이얼' },
];

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const CALENDAR_CELL_COUNT = 42; // 6주 × 7일 고정 — 달마다 그리드 높이가 튀지 않게
const DIAL_OFFSETS = [-2, -1, 0, 1, 2];
const DATE_FORMAT = 'YYYY-MM-DD';

const weekdayTone = dayIndex => (dayIndex === 0 ? 'sun' : dayIndex === 6 ? 'sat' : 'weekday');

const toDotted = value => (value ? value.split('-').join('.') : '');

// "2026.03.01" / "2026-03-01" / "20260301" 모두 받는다. 해석 불가면 null.
const parseDotted = text => {
  const digits = (text || '').replace(/[^0-9]/g, '');
  if (digits.length !== 8) {
    return null;
  }
  const parsed = moment(digits, 'YYYYMMDD', true);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : null;
};

const CalendarIcon = ({ size = 18, color = '#4ab3bc' }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.6" y="3.6" width="14.8" height="12.8" rx="2.6" stroke={color} strokeWidth="1.6" />
    <path d="M1.6 7.6h14.8" stroke={color} strokeWidth="1.6" />
    <path d="M5.6 1.6v3.2M12.4 1.6v3.2" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/**
 * @param tab          일일/주간/월간
 * @param refDate      기준일(moment)
 * @param dateFrom     실제 조회 시작일 "YYYY-MM-DD"
 * @param dateTo       실제 조회 종료일 "YYYY-MM-DD"
 * @param onTabChange  탭 변경 → 상위에서 기준일 기준 범위로 재조회
 * @param onRefDateChange 기준일 이동 → 상위에서 재조회
 * @param onRangeChange   달력/직접 입력으로 임의 구간 지정 → 상위에서 재조회
 */
const SalesManagPeriodBar = ({
  tab,
  custom,
  onTabChange,
  refDate,
  onRefDateChange,
  dateFrom,
  dateTo,
  onRangeChange,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('cal');
  const [monthCursor, setMonthCursor] = useState(moment(refDate));
  // 달력 두 번 클릭으로 기간을 만든다. anchor = 첫 클릭 지점.
  const [anchor, setAnchor] = useState(null);
  // 'from' | 'to' — 직접 입력 칸의 달력 버튼으로 한쪽만 조준할 때.
  const [aim, setAim] = useState(null);
  const [fromText, setFromText] = useState(null);
  const [toText, setToText] = useState(null);
  // 팝오버 안에서 고른 값은 임시로만 들고 있다가 하단 확정 버튼에서 한 번에 조회한다.
  // 날짜를 집을 때마다 조회가 나가면 기간을 만드는 중에 목록이 두 번 갈린다.
  const [draft, setDraft] = useState({ from: dateFrom, to: dateTo });
  const [draftRef, setDraftRef] = useState(moment(refDate));
  // 확정 시 무엇을 반영할지는 '마지막으로 만진 쪽'이 정한다.
  // 모드(달력/다이얼)로 판정하면 다이얼로 고른 뒤 달력 탭만 눌러도 선택이 버려진다.
  const [dialTouched, setDialTouched] = useState(false);
  const anchorRef = useRef(null);

  const base = moment(refDate);

  useEffect(() => {
    if (open) {
      setDraft({ from: dateFrom, to: dateTo });
      setDraftRef(moment(refDate));
      setDialTouched(false);
      setMonthCursor(moment(dateFrom || refDate));
    }
  }, [open, dateFrom, dateTo, refDate]);

  // 바깥을 누르면 닫는다 — 팝오버가 열린 채로 다른 조작을 하게 두지 않는다.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleOutside = event => {
      if (anchorRef.current && !anchorRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const closePopover = () => {
    setOpen(false);
    setDialTouched(false);
    setAnchor(null);
    setAim(null);
    setFromText(null);
    setToText(null);
  };

  // 두 번째 클릭이 앞 날짜여도 되게 순서를 맞춰 담는다.
  const setDraftRange = (from, to) => {
    const [start, end] = moment(from).isAfter(moment(to)) ? [to, from] : [from, to];
    setDraft({ from: start, to: end });
    setDialTouched(false);
  };

  // 조회가 나가는 유일한 지점.
  const confirmPopover = () => {
    if (dialTouched) {
      if (!draftRef.isSame(base, 'day')) {
        onRefDateChange(moment(draftRef));
      }
    } else if (draft.from !== dateFrom || draft.to !== dateTo) {
      onRangeChange(draft.from, draft.to);
    }
    closePopover();
  };

  // ◀ ▶ : 일일/주간/월간이면 기준일을 그 단위만큼, 임의 구간이면 창 길이만큼 통째로 민다.
  const handleStep = direction => {
    if (custom) {
      const length = moment(dateTo).diff(moment(dateFrom), 'days') + 1;
      onRangeChange(
        moment(dateFrom)
          .add(direction * length, 'days')
          .format(DATE_FORMAT),
        moment(dateTo)
          .add(direction * length, 'days')
          .format(DATE_FORMAT),
      );
      return;
    }
    const unit = tab === 'day' ? 'days' : tab === 'week' ? 'weeks' : 'months';
    onRefDateChange(moment(base).add(direction, unit));
  };

  const handleDayClick = iso => {
    if (aim) {
      setDraftRange(aim === 'from' ? iso : draft.from, aim === 'from' ? draft.to : iso);
      setAim(null);
      return;
    }
    if (!anchor) {
      setAnchor(iso);
      setDraft({ from: iso, to: iso });
      setDialTouched(false);
      return;
    }
    setDraftRange(anchor, iso);
    setAnchor(null);
  };

  const handleDialStep = (unit, offset) => {
    if (offset === 0) {
      return;
    }
    const next =
      unit === 'y'
        ? moment(draftRef).add(offset, 'years')
        : unit === 'm'
          ? moment(draftRef).add(offset, 'months')
          : moment(draftRef).add(offset, 'days');
    setDraftRef(next);
    setDialTouched(true);
    setMonthCursor(next);
  };

  const commitManual = (target, text) => {
    const parsed = parseDotted(text);
    if (parsed) {
      setDraftRange(target === 'from' ? parsed : draft.from, target === 'from' ? draft.to : parsed);
    }
    setFromText(null);
    setToText(null);
  };

  const days = moment(dateTo).diff(moment(dateFrom), 'days') + 1;
  const from = moment(dateFrom);
  const to = moment(dateTo);
  const rangeText =
    dateFrom === dateTo
      ? `${from.format('YYYY.MM.DD')} (${WEEKDAYS[from.day()]})`
      : from.isSame(to, 'month')
        ? `${from.format('YYYY.MM.DD')} ~ ${to.format('DD')}`
        : from.isSame(to, 'year')
          ? `${from.format('YYYY.MM.DD')} ~ ${to.format('MM.DD')}`
          : `${from.format('YYYY.MM.DD')} ~ ${to.format('YYYY.MM.DD')}`;

  const gridStart = moment(monthCursor).startOf('month').startOf('week');
  const cells = Array.from({ length: CALENDAR_CELL_COUNT }, (_, index) => {
    const day = moment(gridStart).add(index, 'days');
    const iso = day.format(DATE_FORMAT);
    return {
      iso,
      label: day.date(),
      inMonth: day.month() === monthCursor.month() && day.year() === monthCursor.year(),
      inRange: iso >= draft.from && iso <= draft.to,
      isEdge: iso === draft.from || iso === draft.to,
      tone: weekdayTone(day.day()),
    };
  });

  const dialColumns = [
    { unit: 'y', format: offset => `${moment(draftRef).add(offset, 'years').year()}년` },
    { unit: 'm', format: offset => `${moment(draftRef).add(offset, 'months').month() + 1}월` },
    { unit: 'd', format: offset => `${moment(draftRef).add(offset, 'days').date()}일` },
  ];

  const confirmLabel = dialTouched
    ? `${draftRef.format('M월 D일')}(${WEEKDAYS[draftRef.day()]}) 조회`
    : draft.from === draft.to
      ? `${moment(draft.from).format('M월 D일')}(${WEEKDAYS[moment(draft.from).day()]}) 조회`
      : `${moment(draft.from).format('M월 D일')} ~ ${moment(draft.to).format('M월 D일')} 조회`;

  const hint = aim
    ? aim === 'from'
      ? '달력에서 시작일을 클릭하세요'
      : '달력에서 종료일을 클릭하세요'
    : anchor
      ? '날짜를 한 번 더 클릭하세요'
      : '';

  return (
    <PeriodBarRow>
      <PeriodToggleGroup>
        {TABS.map(item => (
          <PeriodToggleButton
            key={item.key}
            selected={!custom && tab === item.key}
            position={item.position}
            onClick={() => onTabChange(item.key)}>
            {item.label}
          </PeriodToggleButton>
        ))}
      </PeriodToggleGroup>

      <PeriodCenter>
        <PeriodArrow onClick={() => handleStep(-1)}>‹</PeriodArrow>

        <PeriodChipAnchor ref={anchorRef}>
          <PeriodChip open={open} onClick={() => (open ? closePopover() : setOpen(true))}>
            <CalendarIcon />
            <PeriodChipText>{rangeText}</PeriodChipText>
            <PeriodChipDays>총 {days}일</PeriodChipDays>
            <PeriodChipCaret>▼</PeriodChipCaret>
          </PeriodChip>

          {open && (
            <PeriodPopover>
              <PeriodPopHeader>
                <PeriodPopMonth>{monthCursor.format('YYYY년 M월')}</PeriodPopMonth>
                <PeriodPopNav>
                  <PeriodPopNavBtn onClick={() => setMonthCursor(moment(monthCursor).subtract(1, 'month'))}>
                    ‹
                  </PeriodPopNavBtn>
                  <PeriodPopNavBtn onClick={() => setMonthCursor(moment(monthCursor).add(1, 'month'))}>
                    ›
                  </PeriodPopNavBtn>
                </PeriodPopNav>
              </PeriodPopHeader>

              {/* 달력과 다이얼은 같은 팝오버의 두 모드다 — 날짜를 두 군데 표시하지 않는다. */}
              <SegmentTrack>
                {MODES.map(item => (
                  <PeriodModeTab
                    key={item.key}
                    selected={mode === item.key}
                    onClick={() => {
                      setMode(item.key);
                      if (item.key === 'dial') {
                        setAim(null);
                      }
                    }}>
                    {item.label}
                  </PeriodModeTab>
                ))}
              </SegmentTrack>

              {mode === 'cal' && (
                <PeriodCalWrap>
                  <PeriodCalRow>
                    {WEEKDAYS.map((label, index) => (
                      <PeriodCalWeekCell key={label} tone={weekdayTone(index)}>
                        {label}
                      </PeriodCalWeekCell>
                    ))}
                  </PeriodCalRow>
                  <PeriodCalRow>
                    {cells.map(cell => (
                      <PeriodCalCell
                        key={cell.iso}
                        inMonth={cell.inMonth}
                        inRange={cell.inRange}
                        isEdge={cell.isEdge}
                        tone={cell.tone}
                        onClick={() => cell.inMonth && handleDayClick(cell.iso)}>
                        {cell.label}
                      </PeriodCalCell>
                    ))}
                  </PeriodCalRow>
                </PeriodCalWrap>
              )}

              {mode === 'dial' && (
                <PeriodDialWrap>
                  <PeriodDialHighlight />
                  <PeriodDialCols>
                    {dialColumns.map(column => (
                      <PeriodDialCol key={column.unit}>
                        {DIAL_OFFSETS.map(offset => (
                          <PeriodDialItem
                            key={offset}
                            distance={Math.abs(offset)}
                            onClick={() => handleDialStep(column.unit, offset)}>
                            {column.format(offset)}
                          </PeriodDialItem>
                        ))}
                      </PeriodDialCol>
                    ))}
                  </PeriodDialCols>
                </PeriodDialWrap>
              )}

              <PeriodManualBox>
                <PeriodManualHead>
                  <PeriodManualLabel>직접 입력</PeriodManualLabel>
                  {hint && <PeriodManualHint>{hint}</PeriodManualHint>}
                </PeriodManualHead>
                <PeriodManualRow>
                  <PeriodInputBox active={aim === 'from'}>
                    <PeriodInput
                      value={fromText ?? toDotted(draft.from)}
                      placeholder="2026.03.01"
                      onChange={event => setFromText(event.target.value)}
                      onBlur={event => commitManual('from', event.target.value)}
                    />
                    <PeriodAimBtn
                      onClick={() => {
                        setAim(prev => (prev === 'from' ? null : 'from'));
                        setMode('cal');
                        setAnchor(null);
                      }}>
                      <CalendarIcon size={14} color={aim === 'from' ? '#4ab3bc' : '#9aa3a7'} />
                    </PeriodAimBtn>
                  </PeriodInputBox>
                  <PeriodManualTilde>~</PeriodManualTilde>
                  <PeriodInputBox active={aim === 'to'}>
                    <PeriodInput
                      value={toText ?? toDotted(draft.to)}
                      placeholder="2026.03.20"
                      onChange={event => setToText(event.target.value)}
                      onBlur={event => commitManual('to', event.target.value)}
                    />
                    <PeriodAimBtn
                      onClick={() => {
                        setAim(prev => (prev === 'to' ? null : 'to'));
                        setMode('cal');
                        setAnchor(null);
                      }}>
                      <CalendarIcon size={14} color={aim === 'to' ? '#4ab3bc' : '#9aa3a7'} />
                    </PeriodAimBtn>
                  </PeriodInputBox>
                </PeriodManualRow>
              </PeriodManualBox>

              <PeriodConfirmBtn onClick={confirmPopover}>{confirmLabel}</PeriodConfirmBtn>
            </PeriodPopover>
          )}
        </PeriodChipAnchor>

        <PeriodArrow onClick={() => handleStep(1)}>›</PeriodArrow>
      </PeriodCenter>

      <PeriodBaseLabel>기준일 : {base.format('YYYY년 M월 D일')}</PeriodBaseLabel>
    </PeriodBarRow>
  );
};

export default SalesManagPeriodBar;
